/**
 * Guest issuance — the write-intent channel and outcome projection for
 * issuing a complimentary guest ticket. guest-issue.view.ts is zero-I/O:
 * it composes a GuestIssueAction and pushes it to guestIssueAction$;
 * guest-issue.sync.ts (web-side) is the sole subscriber, writing the
 * rules-gated `checkouts/guest__{id}` doc and reconciling ok/error back via
 * guestIssueResult$, keyed by requestId. Once the write lands,
 * guest-issue.sync.ts watches that SAME doc for the server's own verdict
 * (settleOrder wakes the instant `authorisedAt` is stamped, which the guest
 * branch's own rule pins at create — no separate "settle" call from here) and
 * projects it onto guestIssueOutcome/guestIssueRefusalReason — a sold-out
 * type refuses a guest ticket exactly like it refuses a sale.
 */
import { state } from "@mhmo91/schmancy/state";
import { type Checkout, Event } from "@momo/types";
import { Subject } from "rxjs";

export type GuestIssueLine = Checkout.Checkout["payload"]["items"][number];
export type GuestIssueBuyer = NonNullable<
	Checkout.Checkout["payload"]["buyer"]
>;

/** The two write-eligibility bounds a guest issuance composes under, each
 *  pinned by its own authority:
 *  - 1–10 lines, each with a chosen type and qty >= 1 — the Firestore write
 *    rule's own checkoutZeroPricedLinesOk pin (root package.json's
 *    ican.extraMatches), which unrolls the line list line by line.
 *  - a summed qty across those lines of at most Event.MAX_TICKETS_PER_ORDER —
 *    settleCheckout's own total-qty ceiling, which the guest branch shares
 *    with the buyer branch. What sizes that ceiling is the fan-out one
 *    order's fulfillment produces, against the document, request-size and
 *    duration limits the constant's own declaration names.
 *  Single source for both the submit button's disabled state and the
 *  submit guard, so the two read one predicate instead of two that can
 *  silently drift apart. */
export const MAX_GUEST_ISSUE_LINES = 10;

export function guestIssueLinesValid(
	lines: readonly { typeId: string; qty: number }[],
): boolean {
	return (
		lines.length > 0 &&
		lines.length <= MAX_GUEST_ISSUE_LINES &&
		lines.every((l) => l.typeId !== "" && l.qty >= 1) &&
		lines.reduce((sum, l) => sum + l.qty, 0) <= Event.MAX_TICKETS_PER_ORDER
	);
}

/** Parses a qty input's raw text, floored at the write rule's own qty >= 1 pin —
 *  empty or non-numeric input falls back to 1 rather than 0 or NaN. */
export function clampGuestIssueQty(raw: string): number {
	return Math.max(1, Number.parseInt(raw, 10) || 1);
}

/** One line's qty, fitted into [0, Event.MAX_TICKETS_PER_ORDER − every other
 *  line's qty] — the headroom the total-qty ceiling above leaves that line.
 *  The same clamp the storefront basket's own setQty applies, so a qty typed
 *  or stepped past the ceiling lands ON it instead of composing a total the
 *  guard then refuses. */
export function fitGuestIssueQty(qty: number, otherLinesQty: number): number {
	return Math.max(
		0,
		Math.min(qty, Event.MAX_TICKETS_PER_ORDER - otherLinesQty),
	);
}

export type GuestIssueAction = {
	readonly requestId: string;
	/** The organization the dialog opened under. A save that arrives after a
	 *  switch names it, so the write lands there or nowhere. */
	readonly orgId: string | null;
	readonly eventId: string;
	readonly buyer: GuestIssueBuyer;
	readonly lines: readonly GuestIssueLine[];
};
export const guestIssueAction$ = new Subject<GuestIssueAction>();

export type GuestIssueResult =
	| {
			readonly requestId: string;
			readonly ok: true;
			readonly checkoutId: string;
	  }
	| { readonly requestId: string; readonly ok: false; readonly error: string };
export const guestIssueResult$ = new Subject<GuestIssueResult>();

/** "idle" before any issuance this session; "pending" from a successful
 *  write until the server's own settledAt/refusedAt lands; "settled" /
 *  "refused" are the server's verdict, watched live off the created doc. */
export type GuestIssueOutcome = "idle" | "pending" | "settled" | "refused";
export const guestIssueOutcome = state<GuestIssueOutcome>(
	"admin/sale/guest-issue-outcome",
).memory("idle");

export type GuestRefusalReason = NonNullable<
	Checkout.Checkout["refusalReason"]
>;
export const guestIssueRefusalReason = state<GuestRefusalReason | null>(
	"admin/sale/guest-issue-refusal-reason",
).memory(null);

/** The buyer name/email the pending/settled banner names — set by whichever
 *  destination (Guests tab, console Orders tab) fires guestIssueAction$.
 *  Memory-tier alongside guestIssueOutcome/guestIssueRefusalReason: a
 *  component-local field would go blank on a route remount (switching
 *  console destinations tears down and recreates the routed view) while the
 *  outcome singleton it labels survives — the banner would then read
 *  "Tickets issued to , " with the outcome still correct. */
export const guestIssueLastGuest = state<{
	readonly name: string;
	readonly email: string;
}>("admin/sale/guest-issue-last-guest").memory({ name: "", email: "" });

/** "Issue another" / "Try again" — a side channel, not a direct state write
 *  from the view: guest-issue.sync.ts is the sole writer of
 *  guestIssueOutcome/guestIssueRefusalReason, so
 *  resetting back to "idle" folds through the same reconciler rather than
 *  the view touching those cells itself. */
export const guestIssueReset$ = new Subject<void>();
